import React from "react";

// -------- Dictionaries --------
// Every user-visible string in the app lives here. Missing keys fall back to
// the English dictionary so we never render an empty label.

const en = {
    // Nav
    nav_home: "Home",
    nav_programs: "Programs",
    nav_calendar: "Calendar",
    nav_dashboard: "Dashboard",
    nav_login: "Log in",
    nav_signup: "Enroll now",
    nav_signout: "Sign out",
    nav_profile: "Profile",
    nav_parent_view: "My family",

    // Landing
    hero_kicker: "Islamic Center of Federal Way",
    hero_first_payment: "First payment",
    hero_per_child: "/child",
    hero_then_monthly: "then $60/month",
    hero_title: "Weekend Islamic School",
    hero_subtitle: "One home for Quran, Arabic and Islamic Studies — for children Pre-K through 12.",
    hero_cta_register: "Enroll your family",
    hero_cta_learn: "See our programs",
    pillar_quran_title: "Quran & Tajweed",
    pillar_arabic_title: "Arabic Language",
    pillar_studies_title: "Islamic Studies",
    pillar_arabic_body: "Reading, writing and conversation grouped by ability so every child grows.",
    pillar_studies_body: "Seerah, fiqh and character built into an age-appropriate curriculum.",
    community_kicker: "Family-friendly",
    community_title: "Bring the whole family.",
    community_accent: "One account, every child.",
    community_body: "Add every child to a single family account — one login, one bill, one place to see attendance, report cards and announcements.",
    community_cta: "Create a family account",

    // Programs page
    programs_title: "Programs",
    programs_subtitle: "Our weekend school runs September through May.",

    // Calendar page
    calendar_title: "Calendar",
    calendar_subtitle: "Upcoming school days, prayers and community events.",

    // Auth
    email: "Email",
    password: "Password",
    login: "Log in",
    login_title: "Welcome back",
    login_subtitle: "Sign in to manage your family.",
    login_submit: "Sign in",
    no_account: "No account yet?",
    signup: "Sign up",
    signup_title: "Enroll your family",
    signup_subtitle: "Create a parent account for the Weekend School.",
    signup_submit: "Create account",
    already_have_account: "Already have an account?",
    full_name: "Full name",
    phone: "Phone",

    // Parent dashboard shell
    tab_family: "Family",
    tab_tuition: "Tuition",
    tab_attendance: "Attendance",
    tab_report_cards: "Report Cards",
    tab_announcements: "Announcements",
    add_child: "Add child",
    add_child_title: "Add a child",
    include_in_registration: "Include in registration",
    continue_to_payment: "Continue to payment",
    ready_to_register: "Ready to register",
    child_count_one: "child",
    child_count_many: "children",
    total: "Total",
    first_name: "First name",
    last_name: "Last name",
    dob: "Date of birth",
    grade: "Grade",
    gender: "Gender",
    male: "Male",
    female: "Female",
    boys: "Boys",
    girls: "Girls",
    mixed: "Mixed",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    loading: "Loading…",
    please_wait: "Please wait…",
    no_records: "No records yet.",
    search_placeholder: "Search…",

    // Tuition / subscription
    tuition_title: "Monthly tuition",
    tuition_intro: "Set up auto-pay so you never miss a month.",
    turn_on_autopay: "Turn on monthly auto-pay",
    start_autopay: "Start auto-pay",
    cancel_autopay: "Cancel auto-pay",
    autopay_consent: "I authorize ICFW to charge my card monthly.",
    starts: "Starts",
    next_billing: "Next billing",

    // Checkout
    checkout_title: "Payment",
    order_summary: "Order summary",
    total_today: "Total today",
    registration: "Registration",
    first_month_tuition: "First month tuition",
    pay_now: "Pay",
    back_to_dashboard: "Back to dashboard",

    // Attendance
    attendance_report: "Attendance report",
    sessions_recorded: "Sessions recorded",
    class_average: "Class average",
    download_pdf: "Download PDF",
    present: "Present",
    absent: "Absent",
    late: "Late",
    excused: "Excused",
    sessions: "Sessions",

    // Teacher dashboard
    teacher_roster: "Roster",
    teacher_class: "Class",
    teacher_no_class: "You aren't assigned to a class yet.",

    // Admin dashboard
    admin_overview: "Overview",
    admin_classes: "Classes",
    admin_reassign: "Reassign",
    admin_users: "Users",
    admin_parents_dir: "Parents directory",
    admin_students_dir: "Students directory",
    admin_payments: "Payments",
    admin_announcements: "Announcements",
    admin_events: "Events",
    new_class: "New class",
    create_class_title: "Create a class",
    edit_class_title: "Edit class",
    class_name: "Class name",
    teacher: "Teacher",
    room: "Room",
    capacity: "Capacity",
    description: "Description",
    ai_generate: "AI generate",
    generating: "Generating…",
    save_class: "Save class",
    new_invoice: "New invoice",
    create_invoice_title: "Create invoice",
    existing_family: "Existing family",
    new_parent: "New parent",
    parent: "Parent",
    children: "Children",
    paid_in_person: "Paid in person",
    mark_paid: "Mark paid",
    total_paid: "Total paid",
    last_paid: "Last paid",
    contact: "Contact",
    unassigned: "unassigned",

    // Profile settings
    profile_title: "Profile settings",
    profile_account: "Account",
    profile_your_details: "Your details",
    profile_email_immutable: "Contact an admin to change your email.",
    profile_change_password: "Change password",
    profile_current_password: "Current password",
    profile_new_password: "New password",
    profile_confirm_password: "Confirm new password",
    profile_password_hint: "At least 8 characters.",
    profile_save: "Save profile",
    profile_update_password: "Update password",

    // Forgot / reset password
    forgot_title: "Forgot password?",
    forgot_body: "Enter the email you use to sign in and we'll send you a link to choose a new password.",
    forgot_kicker: "Password reset",
    forgot_send: "Send reset link",
    forgot_sending: "Sending…",
    forgot_success: "If {email} matches an account, we've sent a reset link. Check your inbox (and spam folder) — the link expires in 1 hour.",
    forgot_back: "Back to sign in",
    reset_title: "Choose a new password",
    reset_missing: "This reset link is missing its token.",
    reset_request_new: "Request a new one",
    reset_success: "Your password has been updated. Redirecting you to sign in…",
    reset_submit: "Update password",
    reset_updating: "Updating…",

    // Two-factor auth
    twofa_title: "Two-factor authentication",
    twofa_enabled: "Enabled",
    twofa_intro: "Protect your account with a 6-digit code from an authenticator app (Google Authenticator, Authy, 1Password, etc.). Even if someone steals your password, they won't be able to sign in without your phone.",
    twofa_enable: "Enable 2FA",
    twofa_preparing: "Preparing…",
    twofa_step1: "Open your authenticator app.",
    twofa_step2: "Scan this QR code (or paste the setup key manually).",
    twofa_step3: "Enter the 6-digit code the app shows.",
    twofa_setup_key: "Setup key (if you can't scan)",
    twofa_verify_code: "6-digit code from your app",
    twofa_confirm: "Turn on 2FA",
    twofa_verifying: "Verifying…",
    twofa_active: "Two-factor is protecting your account. Keep your recovery codes somewhere safe.",
    twofa_disable: "Turn off 2FA",
    twofa_new_codes: "New recovery codes",
    twofa_generate_new: "Generate new recovery codes",
    twofa_regen_body: "Old recovery codes will stop working immediately.",
    twofa_pwd_label: "Password",
    twofa_code_label: "2FA code (from your app or a current recovery code)",
    twofa_regen_submit: "Generate new codes",
    twofa_disable_title: "Turn off two-factor?",
    twofa_disable_warn: "Your account will rely on the password alone. You can turn 2FA back on any time.",
    twofa_working: "Working…",
    twofa_save_codes: "Save your recovery codes",
    twofa_codes_warn: "These codes will not be shown again. Store them somewhere only you can access — a password manager works great. Each code is single-use.",
    twofa_copy_all: "Copy all",
    twofa_done: "Done",
    twofa_mfa_kicker: "Two-factor authentication",
    twofa_mfa_title: "Enter your code",
    twofa_mfa_body: "Open your authenticator app and enter the current 6-digit code — or a recovery code (like ABCD-EFGH).",
    twofa_verification_code: "Verification code",
    twofa_verify_signin: "Verify & sign in",
    twofa_use_different: "Use a different account",

    // Messages
    messages_title: "Messages",
    messages_inbox: "Inbox",
    messages_new: "New",
    messages_new_message: "New message",
    messages_search_people: "Search people…",
    messages_no_contacts: "No contacts available.",
    messages_start_thread: "Start conversation",
    messages_none: "No conversations yet. Start a new one →",
    messages_select_hint: "Select a conversation, or start a new one.",
    messages_empty_thread: "No messages yet. Say hello 👋",
    messages_send: "Send",
    messages_write_to: "Write to {name}…",

    // Homework
    homework_title: "Homework",
    homework_assign: "Assign homework",
    homework_none: "No homework assigned yet.",
    homework_title_field: "Title",
    homework_instructions: "Instructions",
    homework_due: "Due date",
    homework_attachments: "Attachments",
    homework_save: "Assign homework",
    homework_submissions_lower: "submissions",
    homework_submission: "submission",
    homework_submissions: "Submissions",
    homework_view_subs: "Submissions",
    homework_delete: "Delete",
    homework_delete_confirm: "Delete this homework and all its submissions?",
    homework_no_students: "No students enrolled in this class yet.",
    homework_not_submitted: "Not submitted yet",
    homework_missing: "Missing",
    homework_submitted: "Submitted",
    homework_graded: "Graded",
    homework_to_do: "To do",
    homework_grade: "Grade",
    homework_edit_grade: "Edit grade",
    homework_feedback: "Feedback",
    homework_grade_input: "Grade (A / 95 / ✓)",
    homework_feedback_input: "Feedback…",
    homework_save_grade: "Save grade",
    homework_teacher_feedback: "Teacher feedback",
    homework_your_submission: "Your submission",
    homework_notes_placeholder: "Notes for teacher (optional)…",
    homework_submit: "Submit homework",
    homework_resubmit: "Re-submit",
    homework_submitting: "Submitting…",
    homework_none_for_children: "No homework assigned yet.",

    // Announcements (audience labels)
    audience_everyone: "Everyone",
    audience_parents: "Parents only",
    audience_teachers: "Teachers only",
    audience_teachers_parents: "Teachers and Parents",
    audience_teachers_parents_short: "Teachers & Parents",
    announcement_new: "New announcement",
    announcement_publish: "Publish",
    announcement_title_field: "Title",
    announcement_body_field: "Body",
    announcement_audience: "Audience",

    // Admin sidebar (new)
    admin_teachers_dir: "Teachers",
    admin_volunteers: "Volunteers",
    admin_teacher_attendance: "Teacher attendance",
    admin_messages: "Messages",
    admin_homework: "Homework",
    admin_roles: "Roles",
    admin_superadmin_link: "Super admin",
    admin_control_center: "Control center",
    admin_portal_kicker: "ICFW · Admin portal",

    // Teachers directory
    teachers_dir_title: "Teachers directory",
    teachers_dir_search: "Search teacher by name, email, phone…",
    teachers_dir_none: "No teachers.",
    teachers_dir_classes: "Classes",
    teachers_dir_students: "Students",
    teachers_dir_recent: "Recent attendance",
    teachers_dir_show_archived: "Show archived",
    teachers_dir_hide_archived: "Hide archived",
    teachers_dir_archive: "Archive",
    teachers_dir_restore: "Restore",
    teachers_dir_archive_confirm: "Archive {name}? They'll be removed from active classes and hidden from lists.",
    teachers_dir_archived_badge: "Archived",

    // Volunteers
    volunteers_title: "Volunteers",
    volunteers_invite: "Invite volunteer",
    volunteers_edit: "Edit",
    volunteers_archive: "Archive",
    volunteers_none: "No volunteers yet — invite your first one.",
    volunteers_search: "Search volunteers…",
    volunteers_role_tag: "Role tag",
    volunteers_role_placeholder: "e.g. Event helper, Coach, Ramadan iftar",
    volunteers_notes: "Notes",
    volunteers_new_title: "Invite volunteer",
    volunteers_edit_title: "Edit volunteer",
    volunteers_name_required: "Name is required",
    volunteers_save_changes: "Save changes",
    volunteers_invited: "Volunteer invited",
    volunteers_updated: "Volunteer updated",

    // Teacher attendance
    teacher_att_title: "Teacher attendance",
    teacher_att_date: "Session date",
    teacher_att_save: "Save",
    teacher_att_no_changes: "No changes to save",
    teacher_att_saved: "Saved {count} teacher(s)",
    teacher_att_no_teachers: "No active teachers.",
    teacher_att_self_reports: "Self-reported call-outs for {date}",
    teacher_att_self_reported: "Self-reported",
    teacher_att_marked_by: "Marked by {name}",
    teacher_att_note_placeholder: "Note (optional)",
    teacher_att_my_history: "My attendance history",
    teacher_att_my_none: "No attendance recorded yet.",
    teacher_att_call_in: "Call in absent",
    teacher_att_call_in_title: "Call in absent",
    teacher_att_call_in_body: "Notify admin that you cannot make it. They'll arrange a substitute.",
    teacher_att_reason: "Reason (optional)",
    teacher_att_reason_placeholder: "e.g. Family emergency, unwell…",
    teacher_att_report: "Report absence",
    teacher_att_reporting: "Reporting…",
    teacher_att_reported: "Absence reported to admin",
    teacher_myattendance_tab: "My attendance",

    // Teacher dashboard tabs
    teacher_classroom: "Classroom",
    teacher_announcements: "Announcements",
    teacher_messages: "Messages",
    teacher_homework: "Homework",
    teacher_salaam: "As-salamu alaykum, {name}",
    teacher_role_kicker: "Teacher",

    // Parent dashboard tabs (existing)
    tab_homework: "Homework",
    tab_messages: "Messages",

    // Super admin console
    superadmin_title: "Super admin console",
    superadmin_kicker: "Owner controls",
    superadmin_users_tab: "Users & roles",
    superadmin_admins_tab: "Manage admins",
    superadmin_audit_tab: "Audit log",
    superadmin_gate: "Only super admins can access this page.",
    superadmin_admins_title: "Admin accounts",
    superadmin_new_admin: "New admin",
    superadmin_create_admin_title: "Create admin account",
    superadmin_temp_pw_hint: "Temporary password (they can change it later)",
    superadmin_role_admin: "Admin",
    superadmin_role_superadmin: "Super admin",
    superadmin_create: "Create admin",
    superadmin_creating: "Creating…",
    superadmin_admin_created: "Admin created",
    superadmin_admin_removed: "Admin removed",
    superadmin_confirm_remove: "Delete admin account {email}?",
    superadmin_no_self_delete: "Can't delete yourself",
    superadmin_audit_title: "Recent admin actions",
    superadmin_audit_none: "No admin actions recorded yet.",
    superadmin_audit_did: "did",
    superadmin_audit_on: "on",
    superadmin_reset_totp: "Reset 2FA",
    superadmin_reset_totp_confirm: "Reset 2FA for {name}? They'll need to re-enrol.",
    superadmin_reset_totp_done: "2FA reset",
    superadmin_role_changed: "{name} is now a {role}",
    superadmin_change_role_confirm: "Change {name} from {from} to {to}?",
    superadmin_search: "Search…",
    superadmin_no_users: "No users match.",
    superadmin_role_col_parent: "Parent",
    superadmin_role_col_teacher: "Teacher",
    superadmin_totp_on: "2FA on",
    superadmin_remove: "Remove",

    // Login MFA step already shared above (twofa_mfa_*)
    login_forgot: "Forgot password?",
    login_demo_kicker: "Demo credentials",

    // Sign up (fills in gaps)
    signup_password_hint: "At least 8 characters.",

    // Parent add-child flow already exists — add prorated confirmation
    prorated_child_added: "Child added ✓",
    prorated_placed: "Placed in {class}",
    prorated_placed_with: "Placed in {class} with {teacher}",
    prorated_first_invoice: "Your first invoice",
    prorated_note: "Prorated based on today (day {day} of the month).",
    prorated_reg_note: " Includes the $15 registration fee for this child.",
    prorated_go_pay: "Continue to payment",
    add_child_save_pay: "Save & continue to payment",
    add_child_saving: "Saving…",

    // Attachments
    attach_button: "Attach file",
    attach_uploading: "Uploading…",
    attach_size_hint: "Up to 25 MB each",
    attach_preview: "Preview",

    // Common badges
    badge_paid: "PAID",
    badge_pending: "PENDING",
    badge_failed: "FAILED",

    // Footer
    footer_address_line: "33400 8th Ave S, Federal Way, WA 98003",

    // Language picker
    language: "Language",
    english: "English",
    arabic: "العربية",
};

const ar = {
    nav_home: "الرئيسية",
    nav_programs: "البرامج",
    nav_calendar: "التقويم",
    nav_dashboard: "لوحة التحكم",
    nav_login: "تسجيل الدخول",
    nav_signup: "التسجيل",
    nav_signout: "تسجيل الخروج",
    nav_profile: "الملف الشخصي",
    nav_parent_view: "عائلتي",

    hero_kicker: "المركز الإسلامي في فيدرال واي",
    hero_first_payment: "الدفعة الأولى",
    hero_per_child: "/طفل",
    hero_then_monthly: "ثم 60$ شهرياً",
    hero_title: "مدرسة نهاية الأسبوع الإسلامية",
    hero_subtitle: "بيت واحد للقرآن واللغة العربية والدراسات الإسلامية — للأطفال من الروضة حتى الصف الثاني عشر.",
    hero_cta_register: "سجّل عائلتك",
    hero_cta_learn: "شاهد برامجنا",
    pillar_quran_title: "القرآن والتجويد",
    pillar_arabic_title: "اللغة العربية",
    pillar_studies_title: "الدراسات الإسلامية",
    pillar_arabic_body: "القراءة والكتابة والمحادثة مقسّمة حسب القدرة ليتقدّم كل طفل.",
    pillar_studies_body: "السيرة والفقه وبناء الأخلاق ضمن منهج مناسب للأعمار.",
    community_kicker: "مناسبة للعائلة",
    community_title: "أحضر كل أفراد العائلة.",
    community_accent: "حساب واحد لكل طفل.",
    community_body: "أضف كل طفل إلى حساب عائلي واحد — دخول واحد، فاتورة واحدة، ومكان واحد لمتابعة الحضور والتقارير والإعلانات.",
    community_cta: "أنشئ حساب عائلة",

    programs_title: "البرامج",
    programs_subtitle: "مدرسة نهاية الأسبوع تعمل من سبتمبر إلى مايو.",

    calendar_title: "التقويم",
    calendar_subtitle: "أيام الدراسة القادمة والصلوات والفعاليات.",

    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    login: "تسجيل الدخول",
    login_title: "مرحباً بعودتك",
    login_subtitle: "سجّل الدخول لإدارة عائلتك.",
    login_submit: "دخول",
    no_account: "ليس لديك حساب؟",
    signup: "إنشاء حساب",
    signup_title: "سجّل عائلتك",
    signup_subtitle: "أنشئ حساب ولي أمر لمدرسة نهاية الأسبوع.",
    signup_submit: "إنشاء الحساب",
    already_have_account: "لديك حساب بالفعل؟",
    full_name: "الاسم الكامل",
    phone: "الهاتف",

    tab_family: "العائلة",
    tab_tuition: "الرسوم الدراسية",
    tab_attendance: "الحضور",
    tab_report_cards: "بطاقات التقارير",
    tab_announcements: "الإعلانات",
    add_child: "إضافة طفل",
    add_child_title: "إضافة طفل",
    include_in_registration: "إدراج في التسجيل",
    continue_to_payment: "متابعة إلى الدفع",
    ready_to_register: "جاهز للتسجيل",
    child_count_one: "طفل",
    child_count_many: "أطفال",
    total: "المجموع",
    first_name: "الاسم الأول",
    last_name: "اسم العائلة",
    dob: "تاريخ الميلاد",
    grade: "الصف",
    gender: "الجنس",
    male: "ذكر",
    female: "أنثى",
    boys: "بنين",
    girls: "بنات",
    mixed: "مختلط",
    save: "حفظ",
    cancel: "إلغاء",
    delete: "حذف",
    edit: "تعديل",
    close: "إغلاق",
    loading: "جاري التحميل…",
    please_wait: "الرجاء الانتظار…",
    no_records: "لا توجد سجلات بعد.",
    search_placeholder: "بحث…",

    tuition_title: "الرسوم الشهرية",
    tuition_intro: "فعّل الدفع التلقائي حتى لا تفوتك أي دفعة.",
    turn_on_autopay: "تفعيل الدفع التلقائي الشهري",
    start_autopay: "بدء الدفع التلقائي",
    cancel_autopay: "إلغاء الدفع التلقائي",
    autopay_consent: "أوافق على أن يقوم المركز بسحب المبلغ الشهري من بطاقتي.",
    starts: "يبدأ",
    next_billing: "الفاتورة القادمة",

    checkout_title: "الدفع",
    order_summary: "ملخص الطلب",
    total_today: "المجموع الآن",
    registration: "رسوم التسجيل",
    first_month_tuition: "رسوم الشهر الأول",
    pay_now: "ادفع",
    back_to_dashboard: "العودة إلى لوحة التحكم",

    attendance_report: "تقرير الحضور",
    sessions_recorded: "الجلسات المسجلة",
    class_average: "متوسط الصف",
    download_pdf: "تنزيل PDF",
    present: "حاضر",
    absent: "غائب",
    late: "متأخر",
    excused: "معذور",
    sessions: "الجلسات",

    teacher_roster: "قائمة الفصل",
    teacher_class: "الصف",
    teacher_no_class: "لم يتم تعيينك لصف بعد.",

    admin_overview: "نظرة عامة",
    admin_classes: "الصفوف",
    admin_reassign: "إعادة التوزيع",
    admin_users: "المستخدمون",
    admin_parents_dir: "دليل الآباء",
    admin_students_dir: "دليل الطلاب",
    admin_payments: "المدفوعات",
    admin_announcements: "الإعلانات",
    admin_events: "الفعاليات",
    new_class: "صف جديد",
    create_class_title: "إنشاء صف",
    edit_class_title: "تعديل الصف",
    class_name: "اسم الصف",
    teacher: "المعلم",
    room: "الغرفة",
    capacity: "السعة",
    description: "الوصف",
    ai_generate: "توليد بالذكاء الاصطناعي",
    generating: "جاري التوليد…",
    save_class: "حفظ الصف",
    new_invoice: "فاتورة جديدة",
    create_invoice_title: "إنشاء فاتورة",
    existing_family: "عائلة موجودة",
    new_parent: "ولي أمر جديد",
    parent: "ولي الأمر",
    children: "الأطفال",
    paid_in_person: "مدفوع نقداً",
    mark_paid: "تحديد كمدفوع",
    total_paid: "إجمالي المدفوع",
    last_paid: "آخر دفع",
    contact: "التواصل",
    unassigned: "غير معيّن",

    // Profile settings
    profile_title: "إعدادات الملف الشخصي",
    profile_account: "الحساب",
    profile_your_details: "بياناتك",
    profile_email_immutable: "تواصل مع المسؤول لتغيير البريد الإلكتروني.",
    profile_change_password: "تغيير كلمة المرور",
    profile_current_password: "كلمة المرور الحالية",
    profile_new_password: "كلمة المرور الجديدة",
    profile_confirm_password: "تأكيد كلمة المرور الجديدة",
    profile_password_hint: "٨ أحرف على الأقل.",
    profile_save: "حفظ الملف الشخصي",
    profile_update_password: "تحديث كلمة المرور",

    // Forgot / reset password
    forgot_title: "نسيت كلمة المرور؟",
    forgot_body: "أدخل بريدك الإلكتروني الذي تستخدمه لتسجيل الدخول وسنرسل لك رابطاً لاختيار كلمة مرور جديدة.",
    forgot_kicker: "استعادة كلمة المرور",
    forgot_send: "إرسال رابط الاستعادة",
    forgot_sending: "جارٍ الإرسال…",
    forgot_success: "إذا كان {email} مرتبطاً بحساب، فقد أرسلنا رابط الاستعادة. تحقق من صندوق الوارد (ومجلد الرسائل غير المرغوب فيها) — تنتهي صلاحية الرابط خلال ساعة.",
    forgot_back: "العودة إلى تسجيل الدخول",
    reset_title: "اختر كلمة مرور جديدة",
    reset_missing: "هذا الرابط ينقصه الرمز.",
    reset_request_new: "اطلب رابطاً جديداً",
    reset_success: "تم تحديث كلمة المرور. سيتم توجيهك إلى تسجيل الدخول…",
    reset_submit: "تحديث كلمة المرور",
    reset_updating: "جارٍ التحديث…",

    // Two-factor auth
    twofa_title: "المصادقة الثنائية",
    twofa_enabled: "مُفعّلة",
    twofa_intro: "احمِ حسابك برمز مكوّن من ٦ أرقام من تطبيق مصادقة (Google Authenticator أو Authy أو 1Password). حتى لو سُرقت كلمة مرورك، لن يستطيع أحد تسجيل الدخول من دون هاتفك.",
    twofa_enable: "تفعيل المصادقة الثنائية",
    twofa_preparing: "جارٍ التحضير…",
    twofa_step1: "افتح تطبيق المصادقة.",
    twofa_step2: "امسح رمز QR (أو ألصق مفتاح الإعداد يدوياً).",
    twofa_step3: "أدخل الرمز المكوّن من ٦ أرقام الذي يعرضه التطبيق.",
    twofa_setup_key: "مفتاح الإعداد (إذا لم تستطع المسح)",
    twofa_verify_code: "الرمز المكوّن من ٦ أرقام من التطبيق",
    twofa_confirm: "تفعيل المصادقة الثنائية",
    twofa_verifying: "جارٍ التحقق…",
    twofa_active: "المصادقة الثنائية تحمي حسابك. احتفظ برموز الاستعادة في مكان آمن.",
    twofa_disable: "إيقاف المصادقة الثنائية",
    twofa_new_codes: "رموز استعادة جديدة",
    twofa_generate_new: "توليد رموز استعادة جديدة",
    twofa_regen_body: "ستتوقف رموز الاستعادة القديمة عن العمل فوراً.",
    twofa_pwd_label: "كلمة المرور",
    twofa_code_label: "رمز المصادقة الثنائية (من تطبيقك أو رمز استعادة حالي)",
    twofa_regen_submit: "توليد رموز جديدة",
    twofa_disable_title: "إيقاف المصادقة الثنائية؟",
    twofa_disable_warn: "سيعتمد حسابك على كلمة المرور فقط. يمكنك إعادة تفعيل المصادقة الثنائية في أي وقت.",
    twofa_working: "جارٍ العمل…",
    twofa_save_codes: "احفظ رموز الاستعادة",
    twofa_codes_warn: "لن تُعرض هذه الرموز مرة أخرى. احفظها في مكان لا يصله سواك — مدير كلمات مرور مثالي. كل رمز يُستخدم مرة واحدة فقط.",
    twofa_copy_all: "نسخ الكل",
    twofa_done: "تم",
    twofa_mfa_kicker: "المصادقة الثنائية",
    twofa_mfa_title: "أدخل الرمز",
    twofa_mfa_body: "افتح تطبيق المصادقة وأدخل الرمز الحالي المكوّن من ٦ أرقام — أو رمز استعادة (مثل ABCD-EFGH).",
    twofa_verification_code: "رمز التحقق",
    twofa_verify_signin: "تحقق وسجّل الدخول",
    twofa_use_different: "استخدم حساباً آخر",

    // Messages
    messages_title: "الرسائل",
    messages_inbox: "الوارد",
    messages_new: "جديد",
    messages_new_message: "رسالة جديدة",
    messages_search_people: "ابحث عن شخص…",
    messages_no_contacts: "لا توجد جهات اتصال متاحة.",
    messages_start_thread: "بدء المحادثة",
    messages_none: "لا توجد محادثات بعد. ابدأ واحدة ←",
    messages_select_hint: "اختر محادثة أو ابدأ واحدة جديدة.",
    messages_empty_thread: "لا رسائل بعد. ابدأ بالسلام 👋",
    messages_send: "إرسال",
    messages_write_to: "اكتب إلى {name}…",

    // Homework
    homework_title: "الواجبات",
    homework_assign: "إسناد واجب",
    homework_none: "لم تُسند أي واجبات بعد.",
    homework_title_field: "العنوان",
    homework_instructions: "التعليمات",
    homework_due: "تاريخ التسليم",
    homework_attachments: "المرفقات",
    homework_save: "إسناد الواجب",
    homework_submissions_lower: "تسليمات",
    homework_submission: "تسليم",
    homework_submissions: "التسليمات",
    homework_view_subs: "التسليمات",
    homework_delete: "حذف",
    homework_delete_confirm: "حذف هذا الواجب وجميع تسليماته؟",
    homework_no_students: "لا يوجد طلاب مسجلون في هذا الصف بعد.",
    homework_not_submitted: "لم يُسلَّم بعد",
    homework_missing: "مفقود",
    homework_submitted: "تم التسليم",
    homework_graded: "تم التقييم",
    homework_to_do: "قيد الإنجاز",
    homework_grade: "الدرجة",
    homework_edit_grade: "تعديل الدرجة",
    homework_feedback: "الملاحظات",
    homework_grade_input: "الدرجة (A / 95 / ✓)",
    homework_feedback_input: "ملاحظات…",
    homework_save_grade: "حفظ الدرجة",
    homework_teacher_feedback: "ملاحظات المعلم",
    homework_your_submission: "تسليمك",
    homework_notes_placeholder: "ملاحظات للمعلم (اختياري)…",
    homework_submit: "تسليم الواجب",
    homework_resubmit: "إعادة تسليم",
    homework_submitting: "جارٍ التسليم…",
    homework_none_for_children: "لا توجد واجبات بعد.",

    // Announcements
    audience_everyone: "الجميع",
    audience_parents: "أولياء الأمور فقط",
    audience_teachers: "المعلمون فقط",
    audience_teachers_parents: "المعلمون وأولياء الأمور",
    audience_teachers_parents_short: "المعلمون وأولياء الأمور",
    announcement_new: "إعلان جديد",
    announcement_publish: "نشر",
    announcement_title_field: "العنوان",
    announcement_body_field: "المحتوى",
    announcement_audience: "الجمهور",

    // Admin sidebar
    admin_teachers_dir: "المعلمون",
    admin_volunteers: "المتطوعون",
    admin_teacher_attendance: "حضور المعلمين",
    admin_messages: "الرسائل",
    admin_homework: "الواجبات",
    admin_roles: "الأدوار",
    admin_superadmin_link: "المشرف الأعلى",
    admin_control_center: "مركز التحكم",
    admin_portal_kicker: "ICFW · بوابة المسؤول",

    // Teachers directory
    teachers_dir_title: "دليل المعلمين",
    teachers_dir_search: "بحث عن معلم بالاسم أو البريد أو الهاتف…",
    teachers_dir_none: "لا يوجد معلمون.",
    teachers_dir_classes: "الصفوف",
    teachers_dir_students: "الطلاب",
    teachers_dir_recent: "الحضور الأخير",
    teachers_dir_show_archived: "عرض المؤرشفين",
    teachers_dir_hide_archived: "إخفاء المؤرشفين",
    teachers_dir_archive: "أرشفة",
    teachers_dir_restore: "استعادة",
    teachers_dir_archive_confirm: "أرشفة {name}؟ سيتم إخراجه من الصفوف النشطة وإخفاؤه من القوائم.",
    teachers_dir_archived_badge: "مؤرشف",

    // Volunteers
    volunteers_title: "المتطوعون",
    volunteers_invite: "دعوة متطوع",
    volunteers_edit: "تعديل",
    volunteers_archive: "أرشفة",
    volunteers_none: "لا يوجد متطوعون بعد — ادعُ أول متطوع.",
    volunteers_search: "بحث عن متطوعين…",
    volunteers_role_tag: "الدور",
    volunteers_role_placeholder: "مثال: مساعد فعاليات، مدرب، إفطار رمضان",
    volunteers_notes: "ملاحظات",
    volunteers_new_title: "دعوة متطوع",
    volunteers_edit_title: "تعديل متطوع",
    volunteers_name_required: "الاسم مطلوب",
    volunteers_save_changes: "حفظ التغييرات",
    volunteers_invited: "تمت دعوة المتطوع",
    volunteers_updated: "تم تحديث المتطوع",

    // Teacher attendance
    teacher_att_title: "حضور المعلمين",
    teacher_att_date: "تاريخ الجلسة",
    teacher_att_save: "حفظ",
    teacher_att_no_changes: "لا توجد تغييرات لحفظها",
    teacher_att_saved: "تم حفظ {count} معلم(ين)",
    teacher_att_no_teachers: "لا يوجد معلمون نشطون.",
    teacher_att_self_reports: "غيابات ذاتية لـ {date}",
    teacher_att_self_reported: "مُبلَّغ ذاتياً",
    teacher_att_marked_by: "سُجّل بواسطة {name}",
    teacher_att_note_placeholder: "ملاحظة (اختياري)",
    teacher_att_my_history: "سجل حضوري",
    teacher_att_my_none: "لم يُسجَّل حضور بعد.",
    teacher_att_call_in: "الإبلاغ عن الغياب",
    teacher_att_call_in_title: "الإبلاغ عن الغياب",
    teacher_att_call_in_body: "أخبر المسؤول بأنك لن تتمكن من الحضور. سيتم ترتيب بديل.",
    teacher_att_reason: "السبب (اختياري)",
    teacher_att_reason_placeholder: "مثال: طارئ عائلي، توعّك…",
    teacher_att_report: "إبلاغ عن الغياب",
    teacher_att_reporting: "جارٍ الإبلاغ…",
    teacher_att_reported: "تم إبلاغ المسؤول بالغياب",
    teacher_myattendance_tab: "حضوري",

    // Teacher dashboard
    teacher_classroom: "الفصل",
    teacher_announcements: "الإعلانات",
    teacher_messages: "الرسائل",
    teacher_homework: "الواجبات",
    teacher_salaam: "السلام عليك، {name}",
    teacher_role_kicker: "المعلم",

    // Parent tabs (new)
    tab_homework: "الواجبات",
    tab_messages: "الرسائل",

    // Super admin
    superadmin_title: "لوحة المشرف الأعلى",
    superadmin_kicker: "تحكم المالك",
    superadmin_users_tab: "المستخدمون والأدوار",
    superadmin_admins_tab: "إدارة المسؤولين",
    superadmin_audit_tab: "سجل التدقيق",
    superadmin_gate: "المشرفون الأعلى فقط يمكنهم الوصول إلى هذه الصفحة.",
    superadmin_admins_title: "حسابات المسؤولين",
    superadmin_new_admin: "مسؤول جديد",
    superadmin_create_admin_title: "إنشاء حساب مسؤول",
    superadmin_temp_pw_hint: "كلمة مرور مؤقتة (يمكنه تغييرها لاحقاً)",
    superadmin_role_admin: "مسؤول",
    superadmin_role_superadmin: "مشرف أعلى",
    superadmin_create: "إنشاء المسؤول",
    superadmin_creating: "جارٍ الإنشاء…",
    superadmin_admin_created: "تم إنشاء المسؤول",
    superadmin_admin_removed: "تمت إزالة المسؤول",
    superadmin_confirm_remove: "حذف حساب المسؤول {email}؟",
    superadmin_no_self_delete: "لا يمكنك حذف نفسك",
    superadmin_audit_title: "أحدث إجراءات المسؤولين",
    superadmin_audit_none: "لم تُسجَّل أي إجراءات بعد.",
    superadmin_audit_did: "قام بـ",
    superadmin_audit_on: "على",
    superadmin_reset_totp: "إعادة تعيين المصادقة الثنائية",
    superadmin_reset_totp_confirm: "إعادة تعيين المصادقة الثنائية لـ {name}؟ سيحتاج إلى إعادة التسجيل.",
    superadmin_reset_totp_done: "أُعيدت المصادقة الثنائية",
    superadmin_role_changed: "{name} أصبح الآن {role}",
    superadmin_change_role_confirm: "تغيير {name} من {from} إلى {to}؟",
    superadmin_search: "بحث…",
    superadmin_no_users: "لا توجد نتائج.",
    superadmin_role_col_parent: "ولي أمر",
    superadmin_role_col_teacher: "معلم",
    superadmin_totp_on: "المصادقة الثنائية مُفعّلة",
    superadmin_remove: "إزالة",

    // Login
    login_forgot: "نسيت كلمة المرور؟",
    login_demo_kicker: "بيانات تجريبية",
    signup_password_hint: "٨ أحرف على الأقل.",

    // Prorated add-child confirmation
    prorated_child_added: "تمت إضافة الطفل ✓",
    prorated_placed: "تم إدراجه في {class}",
    prorated_placed_with: "تم إدراجه في {class} مع {teacher}",
    prorated_first_invoice: "فاتورتك الأولى",
    prorated_note: "محسوبة بالتناسب بناءً على تاريخ اليوم (اليوم {day} من الشهر).",
    prorated_reg_note: " تتضمّن رسم تسجيل قدره ١٥$ لهذا الطفل.",
    prorated_go_pay: "متابعة إلى الدفع",
    add_child_save_pay: "حفظ ومتابعة الدفع",
    add_child_saving: "جارٍ الحفظ…",

    // Attachments
    attach_button: "إرفاق ملف",
    attach_uploading: "جارٍ الرفع…",
    attach_size_hint: "حتى ٢٥ ميغابايت لكل ملف",
    attach_preview: "معاينة",

    // Badges
    badge_paid: "مدفوع",
    badge_pending: "قيد الانتظار",
    badge_failed: "فشل",

    footer_address_line: "33400 8th Ave S, Federal Way, WA 98003",

    language: "اللغة",
    english: "English",
    arabic: "العربية",
};

const DICT = { en, ar };
const KEY = "icfw_lang";

const I18nContext = React.createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
    const [lang, setLangState] = React.useState(() => localStorage.getItem(KEY) || "en");

    React.useEffect(() => {
        const html = document.documentElement;
        html.setAttribute("lang", lang);
        html.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
    }, [lang]);

    const setLang = React.useCallback((l) => {
        localStorage.setItem(KEY, l);
        setLangState(l);
    }, []);

    const t = React.useCallback((k, params) => {
        const raw = DICT[lang]?.[k] ?? DICT.en[k] ?? k;
        if (!params) return raw;
        return String(raw).replace(/\{(\w+)\}/g, (_, key) =>
            params[key] !== undefined ? params[key] : `{${key}}`
        );
    }, [lang]);

    return <I18nContext.Provider value={{ lang, t, setLang }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    return React.useContext(I18nContext);
}
